/**
 * <XtsMover> — Renders a single mover positioned along a Part's chain.
 *
 * Phase-1: GLB or Wireframe-AABB placeholder. The mover-world transform is
 * computed each render from `chain` + `partPositionMm`.
 *
 * Phase-6 (selection): forwards click events with the MoverRef and
 * applies an emissive tint to the GLB's materials (via useGltfHighlight)
 * when `selected`. Phase-7 (drive status): the same hook pulses the
 * emissive intensity at 1 Hz when warning / error is set, and a small
 * billboard <DriveStatusIcon> floats above the mover.
 *
 * Phase-7 (drive status): emissive glow when status.warning / status.error.
 */

import React, { Suspense, useContext, useRef } from 'react';
import { Group } from 'three';
import type { GLTF } from 'three-stdlib';
import { useFrame } from '@react-three/fiber';
import type { ThreeEvent } from '@react-three/fiber';
import type { BuiltChain } from '../../geometry/ChainBuilder.js';
import { moverWorldAt } from '../../geometry/ChainBuilder.js';
import { useGltfClone } from './useGltfClone.js';
import { useGltfHighlight } from './useGltfHighlight.js';
import { SelectionTintContext } from './SelectionTintContext.js';
import { DriveStatusIcon } from './DriveStatusIcon.js';
import {
  DEFAULT_ORIGIN_CORRECTION,
  type OriginCorrection,
} from '../../assets/SidecarLoader.js';
import { useMoverSidecar } from './useMoverSidecar.js';
import { MoverPositionStoreContext } from './MoverPositionStore.js';
import { MoverIdLabel } from './MoverIdLabel.js';
import { userToChainPositionMm } from '../../geometry/positionFrame.js';
import type {
  MoverIdLabelOptions,
  MoverType3D,
  PositionFrame,
} from '../../types.js';

const DEG2RAD = Math.PI / 180;

interface XtsMoverProps {
  chain: BuiltChain;
  partPositionMm: number;
  glbUrl: string;
  /**
   * Mover-id used to look up the imperative position store. When the
   * consumer drives positions via `XtsViewer3DRef.setMoverPosition`,
   * the store entry beats `partPositionMm` (which then acts as a fallback
   * for the very first frame and for movers that were never written to
   * the store).
   */
  moverId?: number;
  /** When set, overrides the fetched sidecar's correction (live calibration). */
  originCorrection?: OriginCorrection;
  /** Mover type for sidecar lookup. Required when no override is provided. */
  moverType?: MoverType3D;
  /** Base URL for sidecar lookups. */
  assetsBaseUrl?: string;
  pathLengthMm?: number;
  heightMm?: number;
  selected?: boolean;
  selectionColor?: string;
  driveStatus?: { warning?: boolean; error?: boolean };
  showWarnings?: boolean;
  showErrors?: boolean;
  /**
   * When true, render the mover's `id` as a 3D text label above the
   * mover. Wired through from `DisplayOptions.showMoverIds`. Off by
   * default to keep the scene clean unless the host opts in.
   */
  showMoverId?: boolean;
  /** Style overrides for the mover-id label. */
  moverIdLabelOptions?: MoverIdLabelOptions;
  onClick?: (e: ThreeEvent<MouseEvent>) => void;
  /**
   * XPU positionFrame applied to `partPositionMm` (and to live store
   * reads). When omitted, positions are taken as chain-frame values.
   */
  positionFrame?: PositionFrame;
  /** Children rendered in the mover's frame (e.g. tools, custom assets). */
  children?: React.ReactNode;
}

const XtsMoverContent: React.FC<XtsMoverProps> = ({
  chain,
  partPositionMm,
  moverId,
  glbUrl,
  originCorrection,
  moverType = 'AT9014_0055',
  assetsBaseUrl = '/models',
  pathLengthMm = 70,
  heightMm = 125,
  selected = false,
  selectionColor = '#FFE090',
  driveStatus,
  showWarnings = true,
  showErrors = true,
  showMoverId = false,
  moverIdLabelOptions,
  onClick,
  positionFrame,
  children,
}) => {
  const positionStore = useContext(MoverPositionStoreContext);
  const groupRef = useRef<Group>(null);

  // Imperative pose update — runs every frame, no React render
  // scheduled. The store wins when an entry is present (the consumer
  // drove the position via the ref API); otherwise we fall back to the
  // prop-driven `partPositionMm` from config so the existing contract
  // keeps working unchanged.
  useFrame(({ invalidate }) => {
    if (!groupRef.current) return;
    const userPos =
      moverId !== undefined && positionStore?.has(moverId)
        ? (positionStore.get(moverId) as number)
        : partPositionMm;
    const chainPos = userToChainPositionMm(userPos, positionFrame);
    const pose = moverWorldAt(chain, chainPos);
    if (!pose) return;
    groupRef.current.position.set(
      pose.positionMm[0],
      pose.positionMm[1],
      pose.positionMm[2],
    );
    groupRef.current.rotation.set(0, 0, (pose.yawDeg * Math.PI) / 180);
  });

  const sidecar = useMoverSidecar(moverType, assetsBaseUrl);
  const effectiveCorrection =
    originCorrection ?? sidecar.correction ?? DEFAULT_ORIGIN_CORRECTION;

  const gltf = useGltfClone(glbUrl ?? '');
  // Selection tint (constant) + drive-status blink (pulse) consolidated
  // on the same per-mesh emissive channel. Clone-on-write keeps the
  // shared cache material untouched.
  useGltfHighlight(gltf, {
    selected,
    selectionColor,
    driveStatus,
    showWarnings,
    showErrors,
  });

  const iconKind: 'warning' | 'error' | null =
    driveStatus?.error && showErrors !== false
      ? 'error'
      : driveStatus?.warning && showWarnings !== false
        ? 'warning'
        : null;

  return (
    <group
      ref={groupRef}
      onClick={onClick}
      onPointerDown={(e) => {
        if (onClick) e.stopPropagation();
      }}
    >
      {!glbUrl || !gltf ? (
        <mesh position={[0, 0, 50]}>
          <boxGeometry args={[pathLengthMm, 50, heightMm]} />
          <meshBasicMaterial color="#3D88E0" wireframe />
        </mesh>
      ) : (
        <group
          position={effectiveCorrection.translateMm}
          rotation={[
            effectiveCorrection.rotationDegEuler[0] * DEG2RAD,
            effectiveCorrection.rotationDegEuler[1] * DEG2RAD,
            effectiveCorrection.rotationDegEuler[2] * DEG2RAD,
            'XYZ',
          ]}
        >
          <primitive object={(gltf as GLTF).scene} />
        </group>
      )}
      {iconKind && (
        <DriveStatusIcon
          kind={iconKind}
          positionMm={[0, 0, heightMm + 20]}
        />
      )}
      {showMoverId && moverId !== undefined && (
        <MoverIdLabel
          text={String(moverId)}
          {...(moverIdLabelOptions ? { options: moverIdLabelOptions } : {})}
        />
      )}
      {/* Broadcast our selection state to mover-bound children
       *  (AT8200 tools, custom assets) so they tint with the same
       *  colour. CustomAssetMesh subscribes via SelectionTintContext. */}
      <SelectionTintContext.Provider
        value={{ selected, color: selectionColor }}
      >
        {children}
      </SelectionTintContext.Provider>
    </group>
  );
};

export const XtsMover: React.FC<XtsMoverProps> = (props) => (
  <Suspense
    fallback={
      <group>
        <mesh>
          <boxGeometry args={[70, 50, 125]} />
          <meshBasicMaterial color="#3D88E0" wireframe />
        </mesh>
      </group>
    }
  >
    <XtsMoverContent {...props} />
  </Suspense>
);
